import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AuthProvider } from "react-oidc-context";
import { BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";

import { clearAuthCallbackParams, isLoginEnabled } from "@/api/authSession";
import { getUserManager } from "@/api/userManager";
import AppRouter from "@/base/components/AppRouter";
import MetaTags from "@/base/components/MetaTags";
import config from "@/constants/config";
import { SalesPointContextProvider } from "@/context/SalesPointProvider";
import { ErrorPage } from "@/pages/ErrorPage";

const ConfiguredApp: React.FC = () => {
  const { i18n } = useTranslation();

  const userManager = useMemo(
    () => getUserManager(i18n.language),
    [i18n.language],
  );

  return (
    <SalesPointContextProvider>
      <BrowserRouter>
        <AuthProvider
          userManager={userManager}
          skipSigninCallback={!isLoginEnabled()}
          onSigninCallback={() => {
            clearAuthCallbackParams();
          }}
        >
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </SalesPointContextProvider>
  );
};

const App: React.FC = () => {
  const scope = Sentry.getCurrentScope();

  scope.setTag("apl.vendor", config.tenantConfig.key);
  scope.setTag("apl.location", config.tenantConfig.location);

  Sentry.setContext("tenant", {
    key: config.tenantConfig.key,
    location: config.tenantConfig.location,
  });
  Sentry.setContext("app", {
    defaultLanguage: config.appConfig.defaultLanguage,
    availableLanguages: config.appConfig.availableLanguages.join(", "),
    currency: config.appConfig.currency.currencyCode,
  });
  Sentry.setContext("features", {
    loginEnabled: config.featureFlags.isLoginEnabled,
    showWIP: config.featureFlags.showWIP,
  });

  return (
    <Sentry.ErrorBoundary
      fallback={(errorData) => (
        <ErrorPage
          error={{
            type: "admin",
            messageForAdmin: errorData.error as Error,
            sentry: {
              message: "React error boundary fallback",
              fingerprint: ["error-page", "react-error-boundary"],
              tags: {
                "error_page.source": "react-error-boundary",
              },
              contexts: {
                react_error_boundary: {
                  componentStack: errorData.componentStack,
                },
              },
            },
          }}
        />
      )}
    >
      <MetaTags />
      <ConfiguredApp />
    </Sentry.ErrorBoundary>
  );
};

export default App;
